import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'

import { Flex, Text } from '@chakra-ui/react'
import { Avatar, Menu } from '@saas-ui/react'

export interface MentionItem {
  id: string
  name: string
  image?: string | null
}

interface MentionListProps {
  items: MentionItem[]
  command: (args: { id: string; label: string }) => void
}

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>(
  function MentionList(props, ref) {
    const [selectedIndex, setSelectedIndex] = useState(0)

    const selectItem = (index: number) => {
      const item = props.items[index]

      if (item) {
        props.command({ id: item.id, label: item.name })
      }
    }

    const upHandler = () => {
      setSelectedIndex(
        (selectedIndex + props.items.length - 1) % props.items.length,
      )
    }

    const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % props.items.length)
    }

    const enterHandler = () => {
      selectItem(selectedIndex)
    }

    useEffect(() => setSelectedIndex(0), [props.items])

    useImperativeHandle(ref, () => ({
      onKeyDown({ event }) {
        if (event.key === 'ArrowUp') {
          upHandler()
          return true
        }

        if (event.key === 'ArrowDown') {
          downHandler()
          return true
        }

        if (event.key === 'Enter') {
          enterHandler()
          return true
        }

        return false
      },
    }))

    return (
      <Menu.Root defaultOpen>
        <Menu.Trigger asChild>
          <div />
        </Menu.Trigger>
        <Menu.Content>
          {props.items.length ? (
            props.items.map((item, index) => (
              <Menu.Item
                value={item.id}
                className={index === selectedIndex ? 'is-selected' : ''}
                key={item.id}
                onClick={() => selectItem(index)}
              >
                <Flex align="center" gap="2">
                  <Avatar
                    size="2xs"
                    name={item.name}
                    src={item.image ?? undefined}
                  />
                  <Text>{item.name}</Text>
                </Flex>
              </Menu.Item>
            ))
          ) : (
            <Menu.Item value="no-result">No result</Menu.Item>
          )}
        </Menu.Content>
      </Menu.Root>
    )
  },
)
